import React, { useEffect, useState } from "react";
import { Star, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", rating: 5, comment: "" });
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation();

  const load = () => api.get("/reviews", { params: { approved_only: true } }).then((r) => setReviews(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.comment) return toast.error("Please fill name and review");
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      toast.success(t("reviews.thankYou"));
      setForm({ name: "", rating: 5, comment: "" });
    } catch (e) {
      toast.error("Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-32 pb-12 min-h-screen" data-testid="reviews-page">
      <div className="container-tulsi">
        <div className="text-center mb-12">
          <p className="text-xs tracking-[0.4em] uppercase text-[#D11D21] mb-3">{t("reviews.subtitle")}</p>
          <h1 className="font-serif-display text-6xl text-gold-gradient">{t("reviews.title")}</h1>
          <div className="gold-separator" />
        </div>

        <div className="grid lg:grid-cols-[1fr_400px] gap-8 max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {reviews.map((r, i) => (
              <motion.div key={r.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="glass-card p-6" data-testid={`review-${r.id}`}>
                <div className="flex gap-1 mb-3">{[...Array(r.rating)].map((_, k) => <Star key={k} size={14} className="text-[#C9A227]" fill="currentColor" />)}</div>
                <p className="text-[#6B4A4E] italic mb-4">"{r.comment}"</p>
                <p className="text-sm tracking-widest uppercase text-[#D11D21]">— {r.name}</p>
              </motion.div>
            ))}
            {reviews.length === 0 && <p className="text-center text-[#6B4A4E] col-span-full" data-testid="no-reviews-msg">{t("reviews.noReviews")}</p>}
          </div>

          <form onSubmit={submit} className="glass-card-strong rounded-2xl p-6 h-fit lg:sticky lg:top-28" data-testid="review-form">
            <h3 className="font-serif-display text-2xl text-gold-gradient mb-4">{t("reviews.share")}</h3>
            <input placeholder={t("reviews.yourName")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-[#FFFAEF] border border-[#E8D9BE] rounded-md px-3 py-2 text-sm text-[#2A0708] outline-none focus:border-[#C9A227] mb-3" data-testid="review-name-input" />
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm text-[#6B4A4E]">{t("reviews.rating")}</span>
              {[1, 2, 3, 4, 5].map((n) => (
                <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })} data-testid={`star-${n}`}>
                  <Star size={20} className={n <= form.rating ? "text-[#C9A227]" : "text-[#E8D9BE]"} fill="currentColor" />
                </button>
              ))}
            </div>
            <textarea rows={4} placeholder={t("reviews.tellUs")} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })}
              className="w-full bg-[#FFFAEF] border border-[#E8D9BE] rounded-md px-3 py-2 text-sm text-[#2A0708] outline-none focus:border-[#C9A227] resize-none" data-testid="review-comment-input" />
            <button type="submit" disabled={submitting} className="btn-saffron w-full justify-center mt-4 disabled:opacity-50" data-testid="submit-review-btn">
              <Send size={14} /> {submitting ? "..." : t("reviews.submit")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
